import maskedText from '../constants/maskedText';
import _ from '../constants/gettext';
import { VehicleModel } from '../hooks/queries/useVehicleModels';
import { Order } from '../hooks/queries/useOrderDetail';
import { VehicleModelDetails } from '../hooks/queries/useVehicleModelDetails';
import { ModelProps } from '../pages/types/ModelProps';
import { ModelOptions } from '../pages/types/ModelOptions';

export const mapToModelOptionsProps = (
  vehicleModelDetails: VehicleModelDetails
): ModelOptions => {
  const { vehicleConfigurationAssets, fullModelCode } = vehicleModelDetails;

  return {
    locale: vehicleConfigurationAssets?.locale,
    version: vehicleConfigurationAssets?.version,
    code: fullModelCode,
    EXC: {
      categoryCode: vehicleConfigurationAssets?.exterior?.color?.categoryCode,
      categoryName: vehicleConfigurationAssets?.exterior?.color?.categoryName,
      color: {
        codes: vehicleConfigurationAssets?.exterior?.color?.typeCodes,
        values:
          vehicleConfigurationAssets?.exterior?.color?.typeValues &&
          Object.assign(
            {},
            ...vehicleConfigurationAssets.exterior.color.typeValues.map(
              (x) => ({
                [x.code]: {
                  code: x.code,
                  title: x.title,
                  tileImage: x.tileImage,
                  images: x.images,
                  images2: x.images2,
                  specs: x.specs
                }
              })
            )
          )
      }
    },
    WHL: {
      categoryCode: vehicleConfigurationAssets?.wheel?.categoryCode,
      categoryName: vehicleConfigurationAssets?.wheel?.categoryName,
      codes: vehicleConfigurationAssets?.wheel?.typeCodes,
      values:
        vehicleConfigurationAssets?.wheel?.typeValues &&
        Object.assign(
          {},
          ...vehicleConfigurationAssets.wheel.typeValues.map((x) => ({
            [x.code]: {
              code: x.code,
              title: x.title,
              tileImage: x.tileImage,
              images: x.images,
              imageSets: x.imageSets,
              specs: x.specs,
              exteriorImages: x.exteriorImages && Object.assign({}, ...x.exteriorImages.map((item) => ({
                [item.code]: {
                  code: item.code,
                  images: item.images,
                  images2: item.images2,
                }
              })))
            }
          }))
        )
    },
    INT: {
      categoryCode: vehicleConfigurationAssets?.interior?.color?.categoryCode,
      categoryName: vehicleConfigurationAssets?.interior?.color?.categoryName,
      color: {
        codes: vehicleConfigurationAssets?.interior?.color?.typeCodes,
        values:
          vehicleConfigurationAssets?.interior?.color?.typeValues &&
          Object.assign(
            {},
            ...vehicleConfigurationAssets.interior.color.typeValues.map(
              (x) => ({
                [x.code]: {
                  code: x.code,
                  title: x.title,
                  tileImage: x.tileImage,
                  images: x.images,
                  images2: x.images2,
                  specs: x.specs,
                }
              })
            )
          )
      },
      features: vehicleConfigurationAssets?.interior?.features || []
    }
  };
};

export const mapToSelectedModelProps = (
  vehicleModelDetails: VehicleModelDetails,
  exteriorColorCode?: string,
  wheelCode?: string,
  interiorColorCode?: string
): ModelProps => {
  const {
    vehicleConfigurationAssets: { exterior, interior, wheel }
  } = vehicleModelDetails;

  const selectedExteriorColorCode =
    exteriorColorCode && exterior?.color?.typeCodes?.includes(exteriorColorCode)
      ? exteriorColorCode
      : exterior?.color?.typeCodes?.[0];
  const selectedExteriorColor = exterior?.color?.typeValues?.find(
    (x) => x.code === selectedExteriorColorCode
  );

  const selectedInteriorColorCode =
    interiorColorCode && interior?.color?.typeCodes?.includes(interiorColorCode)
      ? interiorColorCode
      : interior?.color?.typeCodes?.[0];
  const selectedInteriorColor = interior?.color?.typeValues?.find(
    (x) => x.code === selectedInteriorColorCode
  );

  const selectedWheelCode =
    wheelCode && wheel?.typeCodes?.includes(wheelCode)
      ? wheelCode
      : wheel?.typeCodes?.[0];
  const selectedWheel = wheel?.typeValues?.find(
    (x) => x.code === selectedWheelCode
  );

  return {
    code: vehicleModelDetails?.fullModelCode,
    EXC: {
      color: {
        code: selectedExteriorColorCode,
        title: selectedExteriorColor?.title || '',
        tileImage: selectedExteriorColor?.tileImage || '',
        images: selectedExteriorColor?.images || []
      }
    },
    WHL: {
      code: selectedWheelCode,
      title: selectedWheel?.title || '',
      tileImage: selectedWheel?.tileImage || '',
      images: selectedWheel?.images || [],
      imageSets: selectedWheel?.imageSets || [],
      exteriorImages: selectedWheel?.exteriorImages && Object.assign({}, ...selectedWheel.exteriorImages.map((item) => ({
        [item.code]: {
          code: item.code,
          images: item.images
        }
      })))
    },
    INT: {
      color: {
        code: selectedInteriorColorCode,
        title: selectedInteriorColor?.title || '',
        tileImage: selectedInteriorColor?.tileImage || '',
        images: selectedInteriorColor?.images || []
      },
      features: interior?.features || []
    }
  };
};

/**
 * Get vehicle model details by model code.
 * @param {*} modelCode full model code (Eg. FF91FNA, FF91FNA-ALLIANCE)
 * @param {*} vehicleModels vehicle model list
 * @returns
 *  - id: vehicle model Id,
 *  - vehicleMarketStatusDesc: PreOrder, ProductionOrder and FullyBooked etc.
 *  - depositAmount: The deposit amount to make PreOrder or order.
 *  - basePrice: The vehicle base price
 *  - visible: Whether the vehicle model is visible for configurator
 *      1. By default the model is visible only when vehicleMarketStatusDesc equals ProductionOrder
 */
export const getVehicleModelByCode = (
  modelCode: string,
  vehicleModels: VehicleModel[]
): {
  id: number;
  vehicleMarketStatusDesc: string;
  depositAmount: number;
  basePrice: string;
  visible: boolean;
} => {
  const defaultProps = {
    id: 0,
    vehicleMarketStatusDesc: '',
    depositAmount: 0,
    basePrice: maskedText.priceAmount,
    visible: false
  };

  if (!modelCode || !vehicleModels || vehicleModels.length < 1)
    return defaultProps;

  modelCode = String(modelCode).trim().toUpperCase();

  const vehicleModel = vehicleModels.find((x) => modelCode === x.fullModelCode);

  if (!vehicleModel) return defaultProps;

  return {
    id: vehicleModel.id,
    vehicleMarketStatusDesc: vehicleModel.vehicleMarketStatusDesc,
    depositAmount: vehicleModel.depositAmount,
    basePrice: String(vehicleModel.basePrice),
    visible:
      String(vehicleModel?.vehicleMarketStatusDesc).toUpperCase() ===
      'PRODUCTIONORDER'
  };
};

export const isPreorder = (order: Order | undefined) =>
  String(order?.orderStatus?.name).toUpperCase() === 'PREORDERED';

export const getPreorderProps = (
  order: Order,
  models: VehicleModel[]
):
  | {
      availableModels: VehicleModel[];
      downgradeModels: VehicleModel[];
      downgradeButtonText: string;
    }
  | undefined => {
  if (
    !isPreorder(order) ||
    !order?.vehicleModel ||
    !models ||
    models.length < 1
  )
    return undefined;

  const allianceSuffix = '-ALLIANCE';
  const availableModels = order.vehicleModel?.fullModelCode?.endsWith(
    allianceSuffix
  )
    ? models
    : models.filter(
        (model: VehicleModel) => !model.fullModelCode?.endsWith(allianceSuffix)
      );

  if (!availableModels || availableModels.length < 1) return undefined;

  return {
    availableModels,
    downgradeModels: availableModels.filter(
      (model: VehicleModel) =>
        !model.fullModelCode.endsWith(allianceSuffix) &&
        model.fullModelCode !== order.vehicleModel.fullModelCode
    ),
    downgradeButtonText: _('LABEL_PREORDER_DOWNGRADE_BUTTON')
  };
};
